// src/pages/ProjectDetail.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, adminAPI, resourcesAPI, saleInvoicesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Star, Pin,
  Save, Plus, Trash2, X, Check, Pencil, Upload, Download,
  FolderOpen, AlertCircle,
} from 'lucide-react';
import EInvoiceConfigModal from '../components/invoicing/EInvoiceConfigModal';
import ProjectSalesInvoices from '../components/invoicing/ProjectSalesInvoices';
import './TimesheetDetail.css';
import './ProjectDetail.css';

// ── Constants ────────────────────────────────────────────────────
const STATUT_META = {
  EN_COURS: { label: 'Actif',    color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  ARCHIVE:  { label: 'Archivé', color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' },
};
const TYPE_PROJET_OPTIONS = ['', 'REGIE', 'FORFAIT', 'INTERNE', 'PILOTAGE'];
const DEVISES = ['EUR', 'USD', 'GBP', 'DZD', 'TND', 'MAD'];
const METHODES_PAIEMENT = ['', 'Virement', 'Chèque', 'Prélèvement', 'Autre'];
const ACTION_TYPES  = ['NOTE','APPEL','EMAIL','REUNION','RELANCE','AUTRE'];
const DOC_TYPES     = ['CONTRAT','BON_COMMANDE','FACTURE','RAPPORT','AUTRE'];
// BUSINESS_MANAGER = « Ingenieur d'Affaires » dans l'interface — meme liste
// que _PROJECT_WRITE cote API (app/api/endpoints/projects.py)
const RH_ADMIN      = ['SUPER_ADMIN','ADMIN','RH','BUSINESS_MANAGER'];

const EMPTY_PR_FORM = {
  resource_id:'', poste:'', date_debut:'', date_fin:'',
  tjm:'', cjm_prestation:'', nb_jours_vendus:'', ca_ht:'', etat:'', type_prestation:'REGIE',
};

// ── Helpers ──────────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtDt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}
function fmtNum(val, dec = 2) {
  if (val == null || val === '') return '—';
  return Number(val).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtEur(val, devise = 'EUR') {
  if (val == null) return '—';
  return fmtNum(val) + ' ' + devise;
}

// ── Sub-components ───────────────────────────────────────────────
function Field({ label, children, span }) {
  return (
    <div className="pd-field" style={span ? { gridColumn: `1/${span + 1}` } : {}}>
      <div className="pd-field-label">{label}</div>
      <div className="pd-field-value">{children}</div>
    </div>
  );
}
function KPI({ label, value, positive, negative }) {
  const color = positive ? '#16A34A' : negative ? '#EF4444' : 'var(--text)';
  return (
    <div className="pd-kpi">
      <div className="pd-kpi-label">{label}</div>
      <div className="pd-kpi-value" style={{ color }}>{value}</div>
    </div>
  );
}
function SectionTitle({ children }) {
  return <div className="pd-section-title">{children}</div>;
}

// ── Main component ───────────────────────────────────────────────
export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = RH_ADMIN.includes(user?.role);

  const [project,    setProject]   = useState(null);
  const [allProjects,setAllProjects]= useState([]);
  const [loading,    setLoading]   = useState(true);
  const [activeTab,  setActiveTab] = useState('resume');
  const [isDirty,    setIsDirty]   = useState(false);
  const [saving,     setSaving]    = useState(false);
  const [showEInvoiceConfig, setShowEInvoiceConfig] = useState(false);
  const [form,       setForm]      = useState({});

  // Ressources & users for selects
  const [users,     setUsers]     = useState([]);
  const [resources, setResources] = useState([]);

  // Prestations inline add
  const [showPrForm,  setShowPrForm]  = useState(false);
  const [prType,      setPrType]      = useState('REGIE');
  const [prForm,      setPrForm]      = useState(EMPTY_PR_FORM);
  const [editingPrId, setEditingPrId] = useState(null);
  const [editPrForm,  setEditPrForm]  = useState(EMPTY_PR_FORM);
  const [savingPr,    setSavingPr]    = useState(false);

  // Actions (commentaires & historique)
  const [actions,     setActions]     = useState([]);
  const [commentText, setCommentText] = useState('');
  const [savingCom,   setSavingCom]   = useState(false);

  // Documents (Attachements tab)
  const [docs,      setDocs]      = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType,   setDocType]   = useState('AUTRE');
  const [docTitle,  setDocTitle]  = useState('');
  const fileRef = useRef();

  // Contracts
  const [contractsV,    setContractsV]    = useState([]);
  const [contractsA,    setContractsA]    = useState([]);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractForm,  setContractForm]  = useState({ nom:'', ref_externe:'', categorie:'', date_contrat:'', total_general:'', statut:'OUVERT', description:'' });
  const [editContractId,setEditContractId]= useState(null);
  const [savingContract,setSavingContract]= useState(false);

  // Contacts
  const [contacts,      setContacts]      = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm,   setContactForm]   = useState({ nom:'', email:'', type_identifiant:'EMAIL', societe:'', role:'', telephone:'' });
  const [editContactId, setEditContactId] = useState(null);
  const [savingContact, setSavingContact] = useState(false);

  // Échéancier
  const [echeances,     setEcheances]     = useState([]);
  const [showEchForm,   setShowEchForm]   = useState(false);
  const [echForm,       setEchForm]       = useState({ ref_phase:'', type_echeance:'DATE_FIXE', description:'', message_facture:'', date_fixe:'', type_montant:'PCT_FACTURER', pourcentage:'', montant_ht:'', forcer_echeance:false, service:'', statut:'EN_ATTENTE' });
  const [editEchId,     setEditEchId]     = useState(null);
  const [savingEch,     setSavingEch]     = useState(false);

  // Factures de ventes (SaleInvoice)
  const [saleInvoices,  setSaleInvoices]  = useState([]);

  // Factures d'achats
  const [invoices,      setInvoices]      = useState([]);
  const [showInvForm,   setShowInvForm]   = useState(false);
  const [invForm,       setInvForm]       = useState({ type_facture:'ACHAT', ref_externe:'', date_facture:'', description:'', emetteur:'', destinataire:'', intervenants:'', montant_ht:'', taux_tva:'20', montant_ttc:'', montant_paye:'0', montant_ht_projet:'', statut:'EN_ATTENTE' });
  const [editInvId,     setEditInvId]     = useState(null);
  const [savingInv,     setSavingInv]     = useState(false);

  // ── Load data ───────────────────────────────────────────────────
  const loadProject = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsAPI.get(id);
      setProject(res.data);
      setForm(toForm(res.data));
      setIsDirty(false);
    } catch {
      toast.error('Projet introuvable');
      navigate('/projects');
    } finally { setLoading(false); }
  }, [id, navigate]);

  const loadActions = useCallback(async () => {
    try { const r = await projectsAPI.listActions(id); setActions(r.data || []); } catch {}
  }, [id]);

  const loadDocs = useCallback(async () => {
    try { const r = await projectsAPI.listDocs(id); setDocs(r.data || []); } catch {}
  }, [id]);

  const loadContracts = useCallback(async () => {
    try {
      const [rv, ra] = await Promise.all([
        projectsAPI.listContracts(id, 'VENTE'),
        projectsAPI.listContracts(id, 'ACHAT'),
      ]);
      setContractsV(rv.data || []);
      setContractsA(ra.data || []);
    } catch {}
  }, [id]);

  const loadContacts  = useCallback(async () => {
    try { const r = await projectsAPI.listContacts(id);  setContacts(r.data  || []); } catch {}
  }, [id]);

  const loadEcheances = useCallback(async () => {
    try { const r = await projectsAPI.listEcheances(id); setEcheances(r.data || []); } catch {}
  }, [id]);

  const loadInvoices  = useCallback(async () => {
    try { const r = await projectsAPI.listInvoices(id, 'ACHAT'); setInvoices(r.data || []); } catch {}
  }, [id]);

  const loadSaleInvoices = useCallback(async () => {
    try { const r = await saleInvoicesAPI.list({ projet_id: id, limit: 200 }); setSaleInvoices(r.data?.items || []); } catch {}
  }, [id]);

  useEffect(() => {
    loadProject();
    loadActions();
    loadDocs();
    loadContracts();
    loadContacts();
    loadEcheances();
    loadInvoices();
    loadSaleInvoices();
    adminAPI.listUsers().then(r => setUsers(r.data || [])).catch(() => {});
    resourcesAPI.list({ limit: 500 }).then(r => setResources(r.data || [])).catch(() => {});
    projectsAPI.list({ limit: 500 }).then(r => setAllProjects(r.data || [])).catch(() => {});
  }, [loadProject, loadActions, loadDocs, loadContracts, loadContacts, loadEcheances, loadInvoices, loadSaleInvoices]);

  // ── Form helpers ────────────────────────────────────────────────
  function toForm(p) {
    return {
      nom:                   p.nom                   || '',
      statut:                p.statut                || 'EN_COURS',
      type_projet:           p.type_projet           || '',
      client:                p.client               || '',
      client_final:          p.client_final          || '',
      responsable_id:        p.responsable_id        ? String(p.responsable_id) : '',
      devise:                p.devise               || 'EUR',
      date_debut:            p.date_debut           || '',
      date_fin:              p.date_fin             || '',
      agence:                p.agence               || '',
      pole:                  p.pole                 || '',
      commentaire:           p.commentaire          || '',
      message_facture:       p.message_facture       || '',
      unite_commerciale:     p.unite_commerciale     || '',
      termes_paiement_jours: p.termes_paiement_jours != null ? String(p.termes_paiement_jours) : '',
      fin_du_mois:           !!p.fin_du_mois,
      echeance_achats:       p.echeance_achats       || '',
      echeance_ventes:       p.echeance_ventes       || '',
      methode_paiement:      p.methode_paiement      || '',
      bon_de_commande:       p.bon_de_commande       || '',
      numero_contrat:        p.numero_contrat        || '',
      centre_cout:           p.centre_cout           || '',
      reference_interne:     p.reference_interne    || '',
      ca_ht:                 p.ca_ht    != null ? String(p.ca_ht)    : '',
      marge_ht:              p.marge_ht != null ? String(p.marge_ht) : '',
      rentabilite:           p.rentabilite           || '',
      societe_nom:           p.societe_nom           || '',
      ville:                 p.ville                || '',
      adresse:               p.adresse              || '',
      code_postal:           p.code_postal          || '',
      pays:                  p.pays                 || '',
      contact_nom:           p.contact_nom          || '',
      contact_prenom:        p.contact_prenom       || '',
      societe_secteur:       p.societe_secteur       || '',
      etat_motif:            p.etat_motif            || '',
    };
  }

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Nom obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        responsable_id:        form.responsable_id || null,
        date_debut:            form.date_debut     || null,
        date_fin:              form.date_fin       || null,
        ca_ht:                 form.ca_ht          ? parseFloat(form.ca_ht)          : null,
        marge_ht:              form.marge_ht       ? parseFloat(form.marge_ht)       : null,
        termes_paiement_jours: form.termes_paiement_jours ? parseInt(form.termes_paiement_jours) : null,
        prestations:           [],
      };
      await projectsAPI.update(id, payload);
      toast.success('Projet enregistré');
      setIsDirty(false);
      loadProject();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  // ── Prestations ─────────────────────────────────────────────────
  const prPayload = (f) => ({
    resource_id:     f.resource_id    || null,
    poste:           f.poste          || null,
    date_debut:      f.date_debut     || null,
    date_fin:        f.date_fin       || null,
    tjm:             f.tjm            ? parseFloat(f.tjm)            : null,
    cjm_prestation:  f.cjm_prestation ? parseFloat(f.cjm_prestation) : null,
    nb_jours_vendus: f.nb_jours_vendus ? parseFloat(f.nb_jours_vendus) : null,
    ca_ht:           f.ca_ht          ? parseFloat(f.ca_ht)          : null,
    etat:            f.etat           || null,
    type_prestation: f.type_prestation || null,
  });

  const handleCreatePr = async () => {
    setSavingPr(true);
    try {
      await projectsAPI.createPrestation(id, prPayload({ ...prForm, type_prestation: prType }));
      toast.success('Ligne ajoutée');
      setPrForm(EMPTY_PR_FORM);
      setShowPrForm(false);
      loadProject();
    } catch { toast.error('Erreur'); }
    finally { setSavingPr(false); }
  };

  const handleUpdatePr = async (pid) => {
    setSavingPr(true);
    try {
      await projectsAPI.updatePrestation(id, pid, prPayload(editPrForm));
      toast.success('Mise à jour');
      setEditingPrId(null);
      loadProject();
    } catch { toast.error('Erreur'); }
    finally { setSavingPr(false); }
  };

  const handleDeletePr = async (pid) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try { await projectsAPI.deletePrestation(id, pid); toast.success('Supprimé'); loadProject(); }
    catch { toast.error('Erreur'); }
  };

  const startEditPr = (pr) => {
    setEditingPrId(pr.id);
    setEditPrForm({
      resource_id:     pr.resource_id     ? String(pr.resource_id) : '',
      poste:           pr.poste           || '',
      date_debut:      pr.date_debut      || '',
      date_fin:        pr.date_fin        || '',
      tjm:             pr.tjm             != null ? String(pr.tjm)             : '',
      cjm_prestation:  pr.cjm_prestation  != null ? String(pr.cjm_prestation)  : '',
      nb_jours_vendus: pr.nb_jours_vendus != null ? String(pr.nb_jours_vendus) : '',
      ca_ht:           pr.ca_ht           != null ? String(pr.ca_ht)           : '',
      etat:            pr.etat            || '',
      type_prestation: pr.type_prestation || 'REGIE',
    });
  };

  // ── Commentaires ────────────────────────────────────────────────
  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSavingCom(true);
    try {
      await projectsAPI.createAction(id, { action_type: 'NOTE', title: commentText.trim(), description: '', status: 'FAIT' });
      setCommentText('');
      loadActions();
    } catch { toast.error('Erreur'); }
    finally { setSavingCom(false); }
  };

  // ── Documents ───────────────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      fd.append('title', docTitle || file.name);
      await projectsAPI.uploadDoc(id, fd);
      toast.success('Document ajouté');
      setDocTitle('');
      if (fileRef.current) fileRef.current.value = '';
      loadDocs();
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (did) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await projectsAPI.deleteDoc(id, did); loadDocs(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };

  // ── Archive/Terminer ─────────────────────────────────────────────
  const handleToggleStatut = async () => {
    const next = project.statut === 'EN_COURS' ? 'ARCHIVE' : 'EN_COURS';
    const label = next === 'ARCHIVE' ? 'terminer' : 'réactiver';
    if (!window.confirm(`Voulez-vous ${label} ce projet ?`)) return;
    try {
      await projectsAPI.update(id, { ...toForm(project), statut: next, responsable_id: project.responsable_id || null, prestations: [] });
      toast.success(next === 'ARCHIVE' ? 'Projet terminé' : 'Projet réactivé');
      loadProject();
    } catch { toast.error('Erreur'); }
  };

  // ── Contracts CRUD ──────────────────────────────────────────────
  const contractTypeForTab = () => activeTab === 'contrats_v' ? 'VENTE' : 'ACHAT';

  const EMPTY_CONTRACT = { nom:'', ref_externe:'', categorie:'', date_contrat:'', total_general:'', statut:'OUVERT', description:'' };

  const handleSaveContract = async () => {
    const type_contrat = contractTypeForTab();
    const payload = {
      type_contrat,
      nom:           contractForm.nom || (type_contrat === 'VENTE' ? project.nom : project.nom),
      ref_externe:   contractForm.ref_externe  || null,
      categorie:     contractForm.categorie    || null,
      date_contrat:  contractForm.date_contrat || null,
      total_general: contractForm.total_general ? parseFloat(contractForm.total_general) : null,
      statut:        contractForm.statut       || 'OUVERT',
      description:   contractForm.description  || null,
    };
    setSavingContract(true);
    try {
      if (editContractId) {
        await projectsAPI.updateContract(id, editContractId, payload);
        toast.success('Contrat mis à jour');
      } else {
        await projectsAPI.createContract(id, payload);
        toast.success('Contrat ajouté');
      }
      setShowContractForm(false);
      setEditContractId(null);
      setContractForm(EMPTY_CONTRACT);
      loadContracts();
    } catch { toast.error('Erreur'); }
    finally { setSavingContract(false); }
  };

  const handleDeleteContract = async (cid) => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    try { await projectsAPI.deleteContract(id, cid); toast.success('Supprimé'); loadContracts(); }
    catch { toast.error('Erreur'); }
  };

  const startEditContract = (c) => {
    setEditContractId(c.id);
    setContractForm({
      nom:           c.nom           || '',
      ref_externe:   c.ref_externe   || '',
      categorie:     c.categorie     || '',
      date_contrat:  c.date_contrat  || '',
      total_general: c.total_general != null ? String(c.total_general) : '',
      statut:        c.statut        || 'OUVERT',
      description:   c.description   || '',
    });
    setShowContractForm(true);
  };

  // ── Contacts CRUD ───────────────────────────────────────────────
  const EMPTY_CONTACT = { nom:'', email:'', type_identifiant:'EMAIL', societe:'', role:'', telephone:'' };
  const handleSaveContact = async () => {
    if (!contactForm.nom.trim()) return toast.error('Nom requis');
    setSavingContact(true);
    try {
      if (editContactId) { await projectsAPI.updateContact(id, editContactId, contactForm); toast.success('Contact mis à jour'); }
      else               { await projectsAPI.createContact(id, contactForm); toast.success('Contact ajouté'); }
      setShowContactForm(false); setEditContactId(null); setContactForm(EMPTY_CONTACT); loadContacts();
    } catch { toast.error('Erreur'); } finally { setSavingContact(false); }
  };
  const handleDeleteContact = async (cid) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try { await projectsAPI.deleteContact(id, cid); toast.success('Supprimé'); loadContacts(); } catch { toast.error('Erreur'); }
  };
  const startEditContact = (c) => {
    setEditContactId(c.id);
    setContactForm({ nom:c.nom||'', email:c.email||'', type_identifiant:c.type_identifiant||'EMAIL', societe:c.societe||'', role:c.role||'', telephone:c.telephone||'' });
    setShowContactForm(true);
  };

  // ── Échéancier CRUD ──────────────────────────────────────────────
  const EMPTY_ECH = { ref_phase:'', type_echeance:'DATE_FIXE', description:'', message_facture:'', date_fixe:'', type_montant:'PCT_FACTURER', pourcentage:'', montant_ht:'', forcer_echeance:false, service:'', statut:'EN_ATTENTE' };
  const handleSaveEch = async () => {
    setSavingEch(true);
    try {
      const payload = { ...echForm, pourcentage: echForm.pourcentage ? parseFloat(echForm.pourcentage) : null, montant_ht: echForm.montant_ht ? parseFloat(echForm.montant_ht) : null, date_fixe: echForm.date_fixe || null };
      if (editEchId) { await projectsAPI.updateEcheance(id, editEchId, payload); toast.success('Mis à jour'); }
      else           { await projectsAPI.createEcheance(id, payload); toast.success('Échéance ajoutée'); }
      setShowEchForm(false); setEditEchId(null); setEchForm(EMPTY_ECH); loadEcheances();
    } catch { toast.error('Erreur'); } finally { setSavingEch(false); }
  };
  const handleDeleteEch = async (eid) => {
    if (!window.confirm('Supprimer cette échéance ?')) return;
    try { await projectsAPI.deleteEcheance(id, eid); toast.success('Supprimé'); loadEcheances(); } catch { toast.error('Erreur'); }
  };
  const startEditEch = (e) => {
    setEditEchId(e.id);
    setEchForm({ ref_phase:e.ref_phase||'', type_echeance:e.type_echeance||'DATE_FIXE', description:e.description||'', message_facture:e.message_facture||'', date_fixe:e.date_fixe||'', type_montant:e.type_montant||'PCT_FACTURER', pourcentage:e.pourcentage!=null?String(e.pourcentage):'', montant_ht:e.montant_ht!=null?String(e.montant_ht):'', forcer_echeance:!!e.forcer_echeance, service:e.service||'', statut:e.statut||'EN_ATTENTE' });
    setShowEchForm(true);
  };

  // ── Factures CRUD ────────────────────────────────────────────────
  const EMPTY_INV = { type_facture:'ACHAT', ref_externe:'', date_facture:'', description:'', emetteur:'', destinataire:'', intervenants:'', montant_ht:'', taux_tva:'20', montant_ttc:'', montant_paye:'0', montant_ht_projet:'', statut:'EN_ATTENTE' };
  const handleSaveInv = async () => {
    setSavingInv(true);
    try {
      const payload = { ...invForm, montant_ht:invForm.montant_ht?parseFloat(invForm.montant_ht):null, taux_tva:invForm.taux_tva?parseFloat(invForm.taux_tva):20, montant_ttc:invForm.montant_ttc?parseFloat(invForm.montant_ttc):null, montant_paye:invForm.montant_paye?parseFloat(invForm.montant_paye):0, montant_ht_projet:invForm.montant_ht_projet?parseFloat(invForm.montant_ht_projet):null, date_facture:invForm.date_facture||null };
      if (editInvId) { await projectsAPI.updateInvoice(id, editInvId, payload); toast.success('Facture mise à jour'); }
      else           { await projectsAPI.createInvoice(id, payload); toast.success('Facture ajoutée'); }
      setShowInvForm(false); setEditInvId(null); setInvForm(EMPTY_INV); loadInvoices();
    } catch { toast.error('Erreur'); } finally { setSavingInv(false); }
  };
  const handleDeleteInv = async (iid) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try { await projectsAPI.deleteInvoice(id, iid); toast.success('Supprimé'); loadInvoices(); } catch { toast.error('Erreur'); }
  };
  const startEditInv = (inv) => {
    setEditInvId(inv.id);
    setInvForm({ type_facture:inv.type_facture||'ACHAT', ref_externe:inv.ref_externe||'', date_facture:inv.date_facture||'', description:inv.description||'', emetteur:inv.emetteur||'', destinataire:inv.destinataire||'', intervenants:inv.intervenants||'', montant_ht:inv.montant_ht!=null?String(inv.montant_ht):'', taux_tva:inv.taux_tva!=null?String(inv.taux_tva):'20', montant_ttc:inv.montant_ttc!=null?String(inv.montant_ttc):'', montant_paye:inv.montant_paye!=null?String(inv.montant_paye):'0', montant_ht_projet:inv.montant_ht_projet!=null?String(inv.montant_ht_projet):'', statut:inv.statut||'EN_ATTENTE' });
    setShowInvForm(true);
  };

  // ── Navigation dans la liste ────────────────────────────────────
  const currentIdx = allProjects.findIndex(p => String(p.id) === String(id));
  const total = allProjects.length;
  const goTo = (idx) => {
    if (idx < 0 || idx >= total) return;
    navigate('/projects/' + allProjects[idx].id);
  };

  // ── Resource label ──────────────────────────────────────────────
  const resourceLabel = (r) => r.full_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || '—';

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;
  if (!project) return null;

  const sm = STATUT_META[project.statut] || STATUT_META.EN_COURS;
  const prestations = project.prestations || [];
  const lignesRegie   = prestations.filter(p => (p.type_prestation || 'REGIE').toUpperCase() !== 'FORFAIT');
  const lignesForfait = prestations.filter(p => (p.type_prestation || '').toUpperCase() === 'FORFAIT');

  // Indicateurs calculés
  const totalCA    = prestations.reduce((s, p) => s + (p.ca_ht || 0), 0) || project.ca_ht || 0;
  const totalMarge = project.marge_ht || 0;
  const totalJours = prestations.reduce((s, p) => s + (p.nb_jours_vendus || 0), 0);

  const TABS = [
    { key: 'resume',       label: 'Résumé du projet' },
    { key: 'contrats_v',   label: `Contrats de ventes${contractsV.length ? ` (${contractsV.length})` : ''}` },
    { key: 'contrats_a',   label: `Contrats d'achats${contractsA.length ? ` (${contractsA.length})` : ''}` },
    { key: 'factures_v',   label: `Factures de ventes${saleInvoices.length ? ` (${saleInvoices.length})` : ''}` },
    { key: 'factures_a',   label: "Factures d'achats" },
    { key: 'contacts',     label: 'Contacts' },
    { key: 'avancement',   label: 'Avancement' },
    { key: 'echeancier',   label: 'Échéancier' },
    { key: 'rapports',     label: 'Rapports' },
    { key: 'attachements', label: `Attachements${docs.length ? ` (${docs.length})` : ''}` },
  ];

  // Bouton contextuel header selon l'onglet
  const headerActionBtn = () => {
    if (!canEdit) return null;
    const btnStyle = { borderColor: 'var(--orange-500)', color: 'var(--orange-500)' };
    if (activeTab === 'contrats_v') return (
      <button className="pd-btn-config" style={btnStyle}
        onClick={() => { setContractForm({ ...EMPTY_CONTRACT, categorie: 'Prestation forfaitaire ventes' }); setEditContractId(null); setShowContractForm(true); }}>
        <Plus size={13}/> Nouveau contrat ventes
      </button>
    );
    if (activeTab === 'contrats_a') return (
      <button className="pd-btn-config" style={btnStyle}
        onClick={() => { setContractForm({ ...EMPTY_CONTRACT, categorie: 'Prestation forfaitaire achats' }); setEditContractId(null); setShowContractForm(true); }}>
        <Plus size={13}/> Nouveau contrat achats
      </button>
    );
    if (activeTab === 'contacts') return (
      <button className="pd-btn-config" style={btnStyle}
        onClick={() => { setContactForm({ nom:'', email:'', type_identifiant:'EMAIL', societe:'', role:'', telephone:'' }); setEditContactId(null); setShowContactForm(true); }}>
        <Plus size={13}/> Créer un nouveau contact
      </button>
    );
    if (activeTab === 'echeancier') return (
      <button className="pd-btn-config" style={btnStyle}
        onClick={() => { setEchForm({ ref_phase:project.nom, type_echeance:'DATE_FIXE', description:'', message_facture:'', date_fixe:'', type_montant:'PCT_FACTURER', pourcentage:'100', montant_ht:'', forcer_echeance:false, service:'', statut:'EN_ATTENTE' }); setEditEchId(null); setShowEchForm(true); }}>
        <Plus size={13}/> Ajouter une échéance
      </button>
    );
    if (activeTab === 'factures_a') return (<>
      <button className="pd-btn-config" style={btnStyle}
        onClick={() => { setInvForm({...EMPTY_INV, type_facture:'ACHAT'}); setEditInvId(null); setShowInvForm(true); }}>
        Nouvelle facture d'achat
      </button>
      <button className="pd-btn-config" style={btnStyle}
        onClick={() => { setInvForm({...EMPTY_INV, type_facture:'AVOIR_FOURNISSEUR'}); setEditInvId(null); setShowInvForm(true); }}>
        + Avoir fournisseur
      </button>
    </>);
    return null;
  };

  const PrInput = ({ value, onChange, type = 'text', placeholder = '', style = {} }) => (
    <input type={type} className="form-control" value={value} onChange={onChange} placeholder={placeholder}
      style={{ fontSize: '0.75rem', padding: '3px 6px', ...style }}/>
  );

  return (
    <div className="pd-root">

      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      <div className="pd-breadcrumb">
        <button className="pd-breadcrumb-link" onClick={() => navigate('/projects')}>
          Projets
        </button>
        <span className="pd-breadcrumb-sep">›</span>
        <span className="pd-breadcrumb-current">{project.nom}</span>
      </div>

      {/* ── Header card ────────────────────────────────────────────── */}
      <div className="pd-header-card">
        {/* Avatar + identité */}
        <div className="pd-header-identity">
          <div className="pd-avatar">
            <FolderOpen size={22}/>
          </div>
          <div className="pd-header-info">
            <div className="pd-header-title">{project.nom}</div>
            <div className="pd-header-sub">
              Projet créé(e) le {fmtDt(project.boond_created_at || project.created_at)}
            </div>
          </div>
          <span className="pd-status-badge" style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.color}33` }}>
            {sm.label}
          </span>
        </div>

        {/* Navigation + actions */}
        <div className="pd-header-actions">
          <div className="pd-icon-btns">
            <button className="pd-icon-btn" title="Favori"><Star size={15}/></button>
            <button className="pd-icon-btn" title="Épingler"><Pin size={15}/></button>
          </div>
          <div className="pd-nav-arrows">
            <button onClick={() => goTo(currentIdx - 1)} disabled={currentIdx <= 0}><ChevronLeft size={14}/></button>
            <span className="pd-nav-count">{currentIdx >= 0 ? currentIdx + 1 : '?'} / {total}</span>
            <button onClick={() => goTo(currentIdx + 1)} disabled={currentIdx >= total - 1}><ChevronRight size={14}/></button>
          </div>
          {isDirty && (
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={13}/> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          )}
          {headerActionBtn()}
          <button className="pd-btn-config" onClick={() => setShowEInvoiceConfig(true)}>
            <span style={{ fontSize: 13 }}>⚙</span> Config. facture électronique
          </button>
          {canEdit && (
            <button
              className={project.statut === 'EN_COURS' ? 'pd-btn-danger' : 'pd-btn-reactivate'}
              onClick={handleToggleStatut}
            >
              {project.statut === 'EN_COURS' ? '× Terminer le projet' : '↺ Réactiver'}
            </button>
          )}
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────── */}
      <div className="pd-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`pd-tab ${activeTab === t.key ? 'pd-tab--active' : ''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* RÉSUMÉ DU PROJET                                           */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'resume' && (
        <div className="pd-resume">

          {/* Row 1 : PROJET form + INDICATEURS / CONDITIONS / RIB */}
          <div className="pd-resume-top">

            {/* ── LEFT : PROJET ─────────────────────────────────── */}
            <div className="card pd-projet-card">
              <SectionTitle>Projet</SectionTitle>

              <div className="pd-form-grid">
                {/* Nom */}
                <div className="pd-field" style={{ gridColumn: '1/-1' }}>
                  <div className="pd-field-label">Nom du projet <span style={{ color: '#EF4444' }}>*</span></div>
                  <input className="form-control" value={form.nom || ''} onChange={set('nom')} placeholder="Nom du projet"/>
                </div>

                {/* Réf + Type */}
                <div className="pd-field">
                  <div className="pd-field-label">Réf</div>
                  <input className="form-control" value={form.reference_interne || ''} onChange={set('reference_interne')} placeholder="PRJ-001"/>
                </div>
                <div className="pd-field">
                  <div className="pd-field-label">Type</div>
                  <select className="form-control" value={form.type_projet || ''} onChange={set('type_projet')}>
                    {TYPE_PROJET_OPTIONS.map(o => <option key={o} value={o}>{o || '— Sélectionner —'}</option>)}
                  </select>
                </div>

                {/* Client facturé + client final */}
                <div className="pd-field">
                  <div className="pd-field-label">Client facturé</div>
                  <input className="form-control" value={form.client || ''} onChange={set('client')} placeholder="Client"/>
                </div>
                <div className="pd-field">
                  <div className="pd-field-label">Client final</div>
                  <input className="form-control" value={form.client_final || ''} onChange={set('client_final')} placeholder="Client final"/>
                </div>

                {/* Statut */}
                <div className="pd-field">
                  <div className="pd-field-label">Statut</div>
                  <select className="form-control" value={form.statut || 'EN_COURS'} onChange={set('statut')}>
                    <option value="EN_COURS">Actif</option>
                    <option value="ARCHIVE">Archivé</option>
                  </select>
                </div>

                {/* Devise */}
                <div className="pd-field">
                  <div className="pd-field-label">Devise</div>
                  <select className="form-control" value={form.devise || 'EUR'} onChange={set('devise')}>
                    {DEVISES.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                {/* Dates */}
                <div className="pd-field">
                  <div className="pd-field-label">Date de début</div>
                  <input type="date" className="form-control" value={form.date_debut || ''} onChange={set('date_debut')}/>
                </div>
                <div className="pd-field">
                  <div className="pd-field-label">Date de fin</div>
                  <input type="date" className="form-control" value={form.date_fin || ''} onChange={set('date_fin')}/>
                </div>

                {/* Message sur la facture */}
                <div className="pd-field" style={{ gridColumn: '1/-1' }}>
                  <div className="pd-field-label">Message sur la facture</div>
                  <textarea className="form-control" rows={2} value={form.message_facture || ''} onChange={set('message_facture')}
                    placeholder="Texte qui apparaîtra sur la facture" style={{ resize: 'vertical' }}/>
                </div>

                {/* Description */}
                <div className="pd-field" style={{ gridColumn: '1/-1' }}>
                  <div className="pd-field-label">Description</div>
                  <textarea className="form-control" rows={2} value={form.commentaire || ''} onChange={set('commentaire')}
                    placeholder="Description du projet…" style={{ resize: 'vertical' }}/>
                </div>

                {/* Unité commerciale */}
                <div className="pd-field">
                  <div className="pd-field-label">Unité commerciale</div>
                  <input className="form-control" value={form.unite_commerciale || ''} onChange={set('unite_commerciale')} placeholder="Ex: WKEYS"/>
                </div>

                {/* Agence */}
                <div className="pd-field">
                  <div className="pd-field-label">Agence / Site</div>
                  <select className="form-control" value={form.agence || ''} onChange={set('agence')}>
                    <option value="">— Sélectionner —</option>
                    <option value="FRANCE">France</option>
                    <option value="BELGIQUE">Belgique</option>
                    <option value="ALGERIE">Algérie</option>
                    <option value="TUNISIE">Tunisie</option>
                  </select>
                </div>

                {/* Chargé d'affaire */}
                <div className="pd-field" style={{ gridColumn: '1/-1' }}>
                  <div className="pd-field-label">Chargé d'affaire</div>
                  <select className="form-control" value={form.responsable_id || ''} onChange={set('responsable_id')}>
                    <option value="">— Sélectionner —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                  </select>
                </div>
              </div>

              {/* Enregistrer */}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.nom?.trim()}>
                  <Save size={13}/> {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>

            {/* ── RIGHT : INDICATEURS + CONDITIONS + RIB ─────────── */}
            <div className="pd-right-col">

              {/* INDICATEURS */}
              <div className="card pd-indicateurs">
                <SectionTitle>Indicateurs</SectionTitle>
                <div className="pd-kpi-grid">
                  <KPI label="CA total (HT)" value={fmtEur(totalCA, form.devise)} positive={totalCA > 0}/>
                  <KPI label="Marge (HT)"    value={fmtEur(totalMarge, form.devise)} positive={totalMarge > 0} negative={totalMarge < 0}/>
                  <KPI label="Jours vendus"  value={totalJours ? fmtNum(totalJours, 1) + ' j' : '—'}/>
                  <KPI label="Ressources"    value={prestations.length > 0 ? prestations.length + ' profil' + (prestations.length > 1 ? 's' : '') : '—'}/>
                  <KPI label="Rentabilité"   value={project.rentabilite || '—'}/>
                  <KPI label="Ref. interne"  value={project.reference_interne || '—'}/>
                </div>
              </div>

              {/* CONDITIONS DE RÈGLEMENT */}
              <div className="card pd-conditions">
                <SectionTitle>Conditions de règlement</SectionTitle>
                <div className="pd-cond-row">
                  <div className="pd-field-label" style={{ minWidth: 80 }}>Termes</div>
                  <input type="number" className="form-control" style={{ width: 70 }}
                    value={form.termes_paiement_jours || ''} onChange={set('termes_paiement_jours')} placeholder="30"/>
                  <span className="pd-cond-unit">jours</span>
                  <label className="pd-checkbox-label">
                    <input type="checkbox" checked={!!form.fin_du_mois} onChange={set('fin_du_mois')}/>
                    Fin du mois
                  </label>
                </div>
                <div className="pd-cond-row">
                  <div className="pd-field-label" style={{ minWidth: 80 }}>Éch. achats</div>
                  <input className="form-control" style={{ flex: 1 }} value={form.echeance_achats || ''}
                    onChange={set('echeance_achats')} placeholder="30"/>
                </div>
                <div className="pd-cond-row">
                  <div className="pd-field-label" style={{ minWidth: 80 }}>Éch. ventes</div>
                  <input className="form-control" style={{ flex: 1 }} value={form.echeance_ventes || ''}
                    onChange={set('echeance_ventes')} placeholder="30"/>
                </div>
              </div>

              {/* RIB */}
              <div className="card pd-rib">
                <SectionTitle>RIB affiché sur la facture</SectionTitle>
                <div className="pd-cond-row">
                  <div className="pd-field-label" style={{ minWidth: 100 }}>Méthode</div>
                  <select className="form-control" value={form.methode_paiement || ''} onChange={set('methode_paiement')}>
                    {METHODES_PAIEMENT.map(m => <option key={m} value={m}>{m || '— Sélectionner —'}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* ── PHASES DU PROJET ──────────────────────────────────── */}
          <div className="card pd-phases">
            <div className="pd-phases-header">
              <SectionTitle>Phases du projet</SectionTitle>
              {canEdit && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setShowPrForm(v => !v); setPrType('REGIE'); }}>
                  <Plus size={13}/> Nouvelle ligne
                </button>
              )}
            </div>

            {/* Formulaire ajout */}
            {showPrForm && (
              <div className="pd-pr-form">
                <div className="pd-pr-form-row">
                  <div className="pd-field">
                    <div className="pd-field-label">Type</div>
                    <select className="form-control" value={prType} onChange={e => setPrType(e.target.value)}>
                      <option value="REGIE">Régie</option>
                      <option value="FORFAIT">Forfait</option>
                    </select>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Profil / Ressource</div>
                    <select className="form-control" value={prForm.resource_id}
                      onChange={e => setPrForm(f => ({ ...f, resource_id: e.target.value }))}>
                      <option value="">— Aucune —</option>
                      {resources.map(r => <option key={r.id} value={r.id}>{resourceLabel(r)}</option>)}
                    </select>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Titre / Poste</div>
                    <PrInput value={prForm.poste} onChange={e => setPrForm(f => ({ ...f, poste: e.target.value }))} placeholder="Consultant Senior…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Du</div>
                    <PrInput type="date" value={prForm.date_debut} onChange={e => setPrForm(f => ({ ...f, date_debut: e.target.value }))}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Au</div>
                    <PrInput type="date" value={prForm.date_fin} onChange={e => setPrForm(f => ({ ...f, date_fin: e.target.value }))}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Tx vente</div>
                    <PrInput type="number" value={prForm.tjm} onChange={e => setPrForm(f => ({ ...f, tjm: e.target.value }))} placeholder="0" style={{ width: 70 }}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Coût (CJM)</div>
                    <PrInput type="number" value={prForm.cjm_prestation} onChange={e => setPrForm(f => ({ ...f, cjm_prestation: e.target.value }))} placeholder="0" style={{ width: 70 }}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Jours</div>
                    <PrInput type="number" value={prForm.nb_jours_vendus} onChange={e => setPrForm(f => ({ ...f, nb_jours_vendus: e.target.value }))} placeholder="0" style={{ width: 60 }}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">État</div>
                    <PrInput value={prForm.etat} onChange={e => setPrForm(f => ({ ...f, etat: e.target.value }))} placeholder="En cours"/>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowPrForm(false)}>Annuler</button>
                  <button className="btn btn-primary btn-sm" onClick={handleCreatePr} disabled={savingPr}>
                    <Save size={13}/> Enregistrer
                  </button>
                </div>
              </div>
            )}

            {/* Lignes Régie */}
            <div className="pd-phase-section">
              <div className="pd-phase-label">
                Lignes de projet Régie
                {canEdit && (
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8, padding: '2px 8px', fontSize: '0.7rem' }}
                    onClick={() => { setPrType('REGIE'); setShowPrForm(true); }}>
                    <Plus size={11}/> Ajouter
                  </button>
                )}
              </div>
              {lignesRegie.length === 0 ? (
                <div className="pd-empty-row">Aucune ligne de régie</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>État</th>
                        <th>Profil(s)</th>
                        <th>Titre</th>
                        <th>Du</th>
                        <th>Au</th>
                        <th>Tx vente</th>
                        <th>Devise</th>
                        <th>Jours vendus</th>
                        <th>CA HT</th>
                        {canEdit && <th style={{ width: 60 }}/>}
                      </tr>
                    </thead>
                    <tbody>
                      {lignesRegie.map(pr => {
                        const isEdit = editingPrId === pr.id;
                        return (
                          <tr key={pr.id}>
                            <td>
                              {isEdit
                                ? <PrInput value={editPrForm.etat} onChange={e => setEditPrForm(f => ({ ...f, etat: e.target.value }))} style={{ width: 80 }}/>
                                : pr.etat ? <span className="pd-etat-chip">{pr.etat}</span> : '—'}
                            </td>
                            <td style={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                              {isEdit ? (
                                <select className="form-control" style={{ fontSize: '0.75rem', padding: '3px 6px', minWidth: 140 }}
                                  value={editPrForm.resource_id}
                                  onChange={e => setEditPrForm(f => ({ ...f, resource_id: e.target.value }))}>
                                  <option value="">— Aucune —</option>
                                  {resources.map(r => <option key={r.id} value={r.id}>{resourceLabel(r)}</option>)}
                                </select>
                              ) : (
                                [pr.resource_prenom, pr.resource_nom].filter(Boolean).join(' ') || pr.resource_reference || '—'
                              )}
                            </td>
                            <td>
                              {isEdit
                                ? <PrInput value={editPrForm.poste} onChange={e => setEditPrForm(f => ({ ...f, poste: e.target.value }))}/>
                                : <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{pr.poste || '—'}</span>}
                            </td>
                            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                              {isEdit
                                ? <PrInput type="date" value={editPrForm.date_debut} onChange={e => setEditPrForm(f => ({ ...f, date_debut: e.target.value }))}/>
                                : fmt(pr.date_debut)}
                            </td>
                            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                              {isEdit
                                ? <PrInput type="date" value={editPrForm.date_fin} onChange={e => setEditPrForm(f => ({ ...f, date_fin: e.target.value }))}/>
                                : fmt(pr.date_fin)}
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>
                              {isEdit
                                ? <PrInput type="number" value={editPrForm.tjm} onChange={e => setEditPrForm(f => ({ ...f, tjm: e.target.value }))} style={{ width: 70 }}/>
                                : pr.tjm != null ? fmtNum(pr.tjm, 0) : '—'}
                            </td>
                            <td style={{ fontSize: '0.8rem', color: 'var(--text-faint)' }}>{project.devise || 'EUR'}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>
                              {isEdit
                                ? <PrInput type="number" value={editPrForm.nb_jours_vendus} onChange={e => setEditPrForm(f => ({ ...f, nb_jours_vendus: e.target.value }))} style={{ width: 60 }}/>
                                : pr.nb_jours_vendus != null ? fmtNum(pr.nb_jours_vendus, 1) : '—'}
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>
                              {isEdit
                                ? <PrInput type="number" value={editPrForm.ca_ht} onChange={e => setEditPrForm(f => ({ ...f, ca_ht: e.target.value }))} style={{ width: 80 }}/>
                                : pr.ca_ht != null ? fmtNum(pr.ca_ht) : '—'}
                            </td>
                            {canEdit && (
                              <td>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  {isEdit ? (
                                    <>
                                      <button className="ts-btn-remove" onClick={() => handleUpdatePr(pr.id)} disabled={savingPr} style={{ color: '#16A34A' }}><Check size={13}/></button>
                                      <button className="ts-btn-remove" onClick={() => setEditingPrId(null)}><X size={13}/></button>
                                    </>
                                  ) : (
                                    <>
                                      <button className="ts-btn-remove" onClick={() => startEditPr(pr)}><Pencil size={13}/></button>
                                      <button className="ts-btn-remove" onClick={() => handleDeletePr(pr.id)}><Trash2 size={13}/></button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Lignes Forfait */}
            <div className="pd-phase-section">
              <div className="pd-phase-label">
                Lignes de projet Forfait
                {canEdit && (
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8, padding: '2px 8px', fontSize: '0.7rem' }}
                    onClick={() => { setPrType('FORFAIT'); setShowPrForm(true); }}>
                    <Plus size={11}/> Ajouter
                  </button>
                )}
              </div>
              {lignesForfait.length === 0 ? (
                <div className="pd-empty-row">Aucune ligne de forfait</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>État</th>
                        <th>Nom affectation</th>
                        <th>Profil(s)</th>
                        <th>Titre</th>
                        <th>Tx vente</th>
                        <th>CA HT</th>
                        <th>Jours vendus</th>
                        {canEdit && <th style={{ width: 60 }}/>}
                      </tr>
                    </thead>
                    <tbody>
                      {lignesForfait.map(pr => {
                        const isEdit = editingPrId === pr.id;
                        return (
                          <tr key={pr.id}>
                            <td>
                              {isEdit
                                ? <PrInput value={editPrForm.etat} onChange={e => setEditPrForm(f => ({ ...f, etat: e.target.value }))} style={{ width: 80 }}/>
                                : pr.etat ? <span className="pd-etat-chip">{pr.etat}</span> : '—'}
                            </td>
                            <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{pr.reference_interne || '—'}</td>
                            <td style={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                              {isEdit ? (
                                <select className="form-control" style={{ fontSize: '0.75rem', padding: '3px 6px', minWidth: 140 }}
                                  value={editPrForm.resource_id}
                                  onChange={e => setEditPrForm(f => ({ ...f, resource_id: e.target.value }))}>
                                  <option value="">— Aucune —</option>
                                  {resources.map(r => <option key={r.id} value={r.id}>{resourceLabel(r)}</option>)}
                                </select>
                              ) : (
                                [pr.resource_prenom, pr.resource_nom].filter(Boolean).join(' ') || '—'
                              )}
                            </td>
                            <td>
                              {isEdit
                                ? <PrInput value={editPrForm.poste} onChange={e => setEditPrForm(f => ({ ...f, poste: e.target.value }))}/>
                                : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{pr.poste || '—'}</span>}
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>
                              {isEdit
                                ? <PrInput type="number" value={editPrForm.tjm} onChange={e => setEditPrForm(f => ({ ...f, tjm: e.target.value }))} style={{ width: 70 }}/>
                                : pr.tjm != null ? fmtNum(pr.tjm, 0) : '—'}
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>
                              {isEdit
                                ? <PrInput type="number" value={editPrForm.ca_ht} onChange={e => setEditPrForm(f => ({ ...f, ca_ht: e.target.value }))} style={{ width: 80 }}/>
                                : pr.ca_ht != null ? fmtNum(pr.ca_ht) : '—'}
                            </td>
                            <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>
                              {pr.nb_jours_vendus != null ? fmtNum(pr.nb_jours_vendus, 1) : '—'}
                            </td>
                            {canEdit && (
                              <td>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  {isEdit ? (
                                    <>
                                      <button className="ts-btn-remove" onClick={() => handleUpdatePr(pr.id)} disabled={savingPr} style={{ color: '#16A34A' }}><Check size={13}/></button>
                                      <button className="ts-btn-remove" onClick={() => setEditingPrId(null)}><X size={13}/></button>
                                    </>
                                  ) : (
                                    <>
                                      <button className="ts-btn-remove" onClick={() => startEditPr(pr)}><Pencil size={13}/></button>
                                      <button className="ts-btn-remove" onClick={() => handleDeletePr(pr.id)}><Trash2 size={13}/></button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Informations projet facture */}
            <div className="pd-phase-section">
              <div className="pd-phase-label">Informations projet facture</div>
              <div className="pd-infofact-grid">
                <div className="pd-field">
                  <div className="pd-field-label">Centre de coût</div>
                  <input className="form-control" value={form.centre_cout || ''} onChange={set('centre_cout')} placeholder="Centre de coût"/>
                </div>
                <div className="pd-field">
                  <div className="pd-field-label">Bon de commande</div>
                  <input className="form-control" value={form.bon_de_commande || ''} onChange={set('bon_de_commande')} placeholder="N° bon de commande"/>
                </div>
                <div className="pd-field">
                  <div className="pd-field-label">N° de contrat</div>
                  <input className="form-control" value={form.numero_contrat || ''} onChange={set('numero_contrat')} placeholder="N° de contrat"/>
                </div>
              </div>
            </div>
          </div>

          {/* ── COMMENTAIRES ─────────────────────────────────────── */}
          <div className="card pd-commentaires">
            <SectionTitle>Commentaires</SectionTitle>
            <div className="pd-comments-list">
              {actions.filter(a => a.action_type === 'NOTE').map(a => (
                <div key={a.id} className="pd-comment-item">
                  <div className="pd-comment-meta">
                    {fmtDt(a.created_at)}
                  </div>
                  <div className="pd-comment-text">{a.title}</div>
                </div>
              ))}
              {actions.filter(a => a.action_type === 'NOTE').length === 0 && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-faint)', padding: '8px 0' }}>Aucun commentaire</div>
              )}
            </div>
            <div className="pd-comment-input-row">
              <textarea
                className="form-control"
                rows={2}
                placeholder="Ajouter un commentaire…"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                style={{ resize: 'vertical', flex: 1 }}
              />
              <button className="btn btn-primary btn-sm" onClick={handleAddComment} disabled={savingCom || !commentText.trim()}>
                Envoyer
              </button>
            </div>
          </div>

          {/* ── HISTORIQUE ───────────────────────────────────────── */}
          <div className="card pd-historique">
            <SectionTitle>Historique</SectionTitle>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date du statut</th>
                    <th>Statut</th>
                    <th>Par l'utilisateur</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Entrée de création */}
                  <tr>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
                      {fmtDt(project.boond_created_at || project.created_at)}
                    </td>
                    <td>
                      <span className="pd-etat-chip pd-etat-chip--green">Actif</span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {[project.responsable_prenom, project.responsable_nom].filter(Boolean).join(' ') || 'Indéterminé'}
                    </td>
                  </tr>
                  {/* Actions de type changement de statut */}
                  {actions.filter(a => a.action_type === 'RELANCE' || a.title?.toLowerCase().includes('statut')).map(a => (
                    <tr key={a.id}>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{fmtDt(a.created_at)}</td>
                      <td><span className="pd-etat-chip">{a.title}</span></td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ATTACHEMENTS                                               */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'attachements' && (<>
        {/* FICHIERS */}
        <div className="card" style={{ marginBottom: 16 }}>
          <SectionTitle style={{ padding:'16px 20px 12px', marginBottom:0, borderBottom:'1px solid var(--border)' }}>
            Fichiers ({docs.length})
          </SectionTitle>
          {docs.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-faint)', fontSize: '0.875rem' }}>Aucun document</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th/><th>Nom</th><th>Location</th><th>Date</th><th>Version</th><th/></tr></thead>
                <tbody>
                  {docs.map(d => (
                    <tr key={d.id}>
                      <td><AlertCircle size={16} style={{ color:'var(--text-faint)', opacity:0.5 }}/></td>
                      <td style={{ fontWeight:600, fontSize:'0.875rem' }}>
                        {d.file_path ? (
                          <a href={`${window.location.origin.replace(':3000',':8000')}${d.file_path}`} target="_blank" rel="noopener noreferrer"
                            style={{ color:'var(--orange-500)' }}>{d.title || d.file_name}</a>
                        ) : (d.title || d.file_name)}
                      </td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{d.document_type || 'Activité'}</td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-faint)', whiteSpace:'nowrap' }}>{new Date(d.created_at).toLocaleDateString('fr-FR')} {new Date(d.created_at).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>1.0</td>
                      <td style={{ whiteSpace:'nowrap' }}>
                        {d.file_path && <a href={`${window.location.origin.replace(':3000',':8000')}${d.file_path}`} target="_blank" rel="noopener noreferrer" style={{ color:'var(--orange-500)', fontSize:'0.8125rem', marginRight:12 }}><Download size={13} style={{marginRight:4}}/>Télécharger le document</a>}
                        {canEdit && <button className="ts-btn-remove" onClick={() => handleDeleteDoc(d.id)}>Supprimer</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* AJOUTER UN DOCUMENT */}
        {canEdit && (
          <div className="card" style={{ marginBottom: 16 }}>
            <SectionTitle style={{ padding:'16px 20px 12px', marginBottom:0, borderBottom:'1px solid var(--border)' }}>Ajouter un document</SectionTitle>
            <div style={{ padding:20, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, alignItems:'start' }}>
              {/* Zone upload — div, pas label, pour éviter que les selects/inputs intérieurs déclenchent le file picker */}
              <div style={{ border:'2px dashed var(--border)', borderRadius:8, padding:'28px 20px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                <Upload size={28} style={{ color:'var(--text-faint)' }}/>
                <div style={{ fontSize:'0.875rem', color:'var(--text-muted)' }}>Faites glisser vos documents ici</div>
                <div style={{ fontSize:'0.75rem', color:'var(--text-faint)' }}>Ou choisissez un type et un titre, puis cliquez sur le bouton</div>
                <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', justifyContent:'center' }}>
                  <select className="form-control" style={{ width:140 }} value={docType} onChange={e => setDocType(e.target.value)}>
                    {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input className="form-control" style={{ width:180 }} placeholder="Titre (optionnel)" value={docTitle} onChange={e => setDocTitle(e.target.value)}/>
                </div>
                <button className="btn btn-primary btn-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                  <Upload size={13}/> {uploading ? 'Envoi en cours…' : 'Choisir un fichier'}
                </button>
                <input ref={fileRef} type="file" style={{ display:'none' }} onChange={handleUpload} disabled={uploading} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"/>
              </div>
              <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'32px 20px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
                <FolderOpen size={28} style={{ color:'var(--text-faint)' }}/>
                <button className="btn btn-secondary btn-sm" style={{ marginTop:8 }} onClick={() => toast('GED non disponible dans cette version')}>Attacher un document depuis la GED</button>
              </div>
            </div>
          </div>
        )}

        {/* COLLECTES DE SIGNATURES */}
        <div className="card">
          <SectionTitle style={{ padding:'16px 20px 12px', marginBottom:0, borderBottom:'1px solid var(--border)' }}>Collectes de signatures</SectionTitle>
          <div className="table-wrap">
            <table>
              <thead><tr><th><input type="checkbox" disabled/></th><th>Description</th><th>Signataires</th><th>Date de création</th><th>Date d'envoi</th><th>Statut</th></tr></thead>
              <tbody><tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune donnée trouvée</td></tr></tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* CONTACTS                                                    */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'contacts' && (<>
        {/* Formulaire ajout/édition */}
        {showContactForm && (
          <div className="card" style={{ marginBottom:16, padding:20 }}>
            <SectionTitle style={{ marginBottom:16 }}>
              {editContactId ? 'Modifier le contact' : 'Ajouter un contact au projet'}
            </SectionTitle>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12, marginBottom:14 }}>
              <div className="pd-field" style={{ gridColumn:'1/-1' }}>
                <div className="pd-field-label">Nom complet *</div>
                <input className="form-control" value={contactForm.nom} onChange={e => setContactForm(f=>({...f,nom:e.target.value}))} placeholder="Nom du contact"/>
              </div>
              <div className="pd-field">
                <div className="pd-field-label">Identifiant de connexion</div>
                <input className="form-control" value={contactForm.email} onChange={e => setContactForm(f=>({...f,email:e.target.value}))} placeholder="email@exemple.com"/>
              </div>
              <div className="pd-field">
                <div className="pd-field-label">Type identifiant</div>
                <select className="form-control" value={contactForm.type_identifiant} onChange={e => setContactForm(f=>({...f,type_identifiant:e.target.value}))}>
                  {['EMAIL','TELEPHONE','NOM_PRENOM','AUTRE'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="pd-field">
                <div className="pd-field-label">Société associée</div>
                <input className="form-control" value={contactForm.societe} onChange={e => setContactForm(f=>({...f,societe:e.target.value}))} placeholder="Société"/>
              </div>
              <div className="pd-field">
                <div className="pd-field-label">Rôle</div>
                <input className="form-control" value={contactForm.role} onChange={e => setContactForm(f=>({...f,role:e.target.value}))} placeholder="Rôle"/>
              </div>
              <div className="pd-field">
                <div className="pd-field-label">Téléphone</div>
                <input className="form-control" value={contactForm.telephone} onChange={e => setContactForm(f=>({...f,telephone:e.target.value}))} placeholder="+33…"/>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowContactForm(false); setEditContactId(null); }}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={handleSaveContact} disabled={savingContact || !contactForm.nom.trim()}>
                <Save size={13}/> {savingContact ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {/* Tableau contacts */}
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID Acteur</th>
                  <th>Nom</th>
                  <th>Identifiant de connexion</th>
                  <th>Type identifiant</th>
                  <th>Société associée</th>
                  <th>Date de création</th>
                  {canEdit && <th style={{ width:60 }}/>}
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--text-faint)' }}>Aucune donnée trouvée</td></tr>
                ) : contacts.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontFamily:'monospace', fontSize:'0.8125rem', color:'var(--orange-500)', cursor:'pointer' }} onClick={() => startEditContact(c)}>
                      {String(c.id).slice(0,6).toUpperCase()}
                    </td>
                    <td style={{ fontWeight:600 }}>{c.nom}</td>
                    <td style={{ fontSize:'0.8125rem' }}>{c.email || '—'}</td>
                    <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{c.type_identifiant || '—'}</td>
                    <td style={{ fontSize:'0.8125rem' }}>{c.societe || '—'}</td>
                    <td style={{ fontSize:'0.8rem', color:'var(--text-faint)' }}>{new Date(c.created_at).toLocaleDateString('fr-FR')}</td>
                    {canEdit && <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="ts-btn-remove" onClick={() => startEditContact(c)}><Pencil size={13}/></button>
                        <button className="ts-btn-remove" onClick={() => handleDeleteContact(c.id)}><Trash2 size={13}/></button>
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* AVANCEMENT                                                  */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'avancement' && (
        <div className="card">
          <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div className="pd-section-title" style={{ marginBottom:0 }}>Phases du projet</div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Projet</th>
                  <th>Du</th>
                  <th>Au</th>
                  <th style={{ textAlign:'right' }}>Charge initiale</th>
                  <th style={{ textAlign:'right' }}>Total consommé</th>
                  <th style={{ textAlign:'right' }}>Total ajusté</th>
                  <th style={{ textAlign:'right' }}>Reste à faire</th>
                  <th style={{ textAlign:'right' }}>Avancement</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {prestations.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:'var(--text-faint)' }}>Aucune phase définie</td></tr>
                ) : prestations.map(p => {
                  const charge = p.nb_jours_vendus || 0;
                  const consomme = 0;
                  const reste = Math.max(0, charge - consomme);
                  const avancement = charge > 0 ? Math.round((consomme / charge) * 100) : 0;
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight:600 }}>{p.poste || project.nom}</td>
                      <td style={{ fontSize:'0.8125rem' }}>{p.date_debut ? fmt(p.date_debut) : '—'}</td>
                      <td style={{ fontSize:'0.8125rem' }}>{p.date_fin ? fmt(p.date_fin) : '—'}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(charge, 2)}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(consomme, 2)}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(charge, 2)}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(reste, 2)}</td>
                      <td style={{ textAlign:'right' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'flex-end' }}>
                          <div style={{ width:60, height:6, borderRadius:3, background:'var(--border)' }}>
                            <div style={{ width:`${avancement}%`, height:'100%', borderRadius:3, background:'var(--orange-500)' }}/>
                          </div>
                          <span style={{ fontSize:'0.8125rem', fontVariantNumeric:'tabular-nums' }}>{avancement}%</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ padding:'2px 8px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, background:'var(--success-100)', color:'var(--success-600)' }}>
                          {p.etat || 'EN COURS'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ÉCHÉANCIER                                                  */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'echeancier' && (() => {
        const TYPES_ECH = ['DATE_FIXE', 'AVANCEMENT', 'LIVRAISON'];
        const TYPES_MONTANT = ['PCT_FACTURER', 'MONTANT_FIXE'];
        return (<>
          {/* Tableau des échéances */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--border)' }}>
              <div className="pd-section-title" style={{ marginBottom:0 }}>{project.nom} :</div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>À l'avancement</th>
                    <th style={{ textAlign:'right' }}>% à facturer</th>
                    <th style={{ textAlign:'right' }}>À facturer</th>
                    <th style={{ textAlign:'right' }}>Facturation</th>
                    <th>Date fixe</th>
                    <th>Message facture</th>
                    <th>Description</th>
                    <th>Statut</th>
                    {canEdit && <th/>}
                  </tr>
                </thead>
                <tbody>
                  {echeances.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign:'center', padding:32, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune échéance</td></tr>
                  ) : echeances.map(e => (
                    <tr key={e.id}>
                      <td style={{ fontSize:'0.8125rem' }}>{e.type_echeance === 'AVANCEMENT' ? '✓' : '—'}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{e.pourcentage != null ? `${e.pourcentage}%` : '—'}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{e.montant_ht != null ? `${fmtNum(e.montant_ht)} €` : '0,00 €'}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>0,00 €</td>
                      <td style={{ fontSize:'0.8125rem' }}>{e.date_fixe ? fmt(e.date_fixe) : '—'}</td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.message_facture || '—'}</td>
                      <td style={{ fontSize:'0.8125rem', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.description || '—'}</td>
                      <td>
                        <span style={{ padding:'2px 8px', borderRadius:12, fontSize:'0.72rem', fontWeight:700,
                          color: e.statut === 'FACTUREE' ? '#16A34A' : e.statut === 'PAYEE' ? '#3B82F6' : '#F59E0B',
                          background: e.statut === 'FACTUREE' ? 'rgba(34,197,94,0.10)' : e.statut === 'PAYEE' ? 'rgba(59,130,246,0.10)' : 'rgba(245,158,11,0.10)'
                        }}>{e.statut}</span>
                      </td>
                      {canEdit && <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="ts-btn-remove" onClick={() => startEditEch(e)}><Pencil size={13}/></button>
                          <button className="ts-btn-remove" onClick={() => handleDeleteEch(e.id)}><Trash2 size={13}/></button>
                        </div>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Formulaire + Masse */}
          {canEdit && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {/* Simple */}
              <div className="card" style={{ padding:20 }}>
                <SectionTitle style={{ marginBottom:16 }}>{showEchForm && editEchId ? 'Modifier l\'échéance' : 'Ajouter une échéance'}</SectionTitle>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <div className="pd-field">
                    <div className="pd-field-label">Réf. de phase *</div>
                    <input className="form-control" value={echForm.ref_phase} onChange={e=>setEchForm(f=>({...f,ref_phase:e.target.value}))} placeholder={project.nom}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Type *</div>
                    <select className="form-control" value={echForm.type_echeance} onChange={e=>setEchForm(f=>({...f,type_echeance:e.target.value}))}>
                      {TYPES_ECH.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
                    </select>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Description de l'échéance</div>
                    <input className="form-control" value={echForm.description} onChange={e=>setEchForm(f=>({...f,description:e.target.value}))} placeholder="Description…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Date fixe *</div>
                    <input type="date" className="form-control" value={echForm.date_fixe} onChange={e=>setEchForm(f=>({...f,date_fixe:e.target.value}))}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Type montant</div>
                    <select className="form-control" value={echForm.type_montant} onChange={e=>setEchForm(f=>({...f,type_montant:e.target.value}))}>
                      {TYPES_MONTANT.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
                    </select>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">% *</div>
                    <input type="number" className="form-control" value={echForm.pourcentage} onChange={e=>setEchForm(f=>({...f,pourcentage:e.target.value}))} placeholder="100" min="0" max="100"/>
                  </div>
                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
                    {editEchId && <button className="btn btn-secondary btn-sm" onClick={() => { setEditEchId(null); setEchForm(EMPTY_ECH); }}>Annuler</button>}
                    <button className="btn btn-primary btn-sm" onClick={handleSaveEch} disabled={savingEch}>
                      {savingEch ? 'Enregistrement…' : editEchId ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Masse */}
              <div className="card" style={{ padding:20 }}>
                <SectionTitle style={{ marginBottom:16 }}>Ajouter des échéances en masse</SectionTitle>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <div className="pd-field">
                    <div className="pd-field-label">Réf. de phase *</div>
                    <input className="form-control" defaultValue={project.nom} readOnly style={{ opacity:0.7 }}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Description de l'échéance</div>
                    <input className="form-control" placeholder="Description…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Du *</div>
                    <input type="date" className="form-control"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Au *</div>
                    <input type="date" className="form-control"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Le</div>
                    <input type="number" className="form-control" placeholder="Jour du mois" min="1" max="31"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Type montant</div>
                    <select className="form-control">
                      {TYPES_MONTANT.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
                    </select>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">% *</div>
                    <input type="number" className="form-control" placeholder="100" min="0" max="100"/>
                  </div>
                  <button className="btn btn-primary btn-sm" style={{ alignSelf:'flex-start' }}
                    onClick={() => toast('Génération en masse non disponible dans cette version')}>
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          )}
        </>);
      })()}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* RAPPORTS                                                    */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'rapports' && (
        <div className="card">
          <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
            <div className="pd-section-title" style={{ marginBottom:0 }}>Tableau de bord Régie</div>
            <ChevronLeft size={16} style={{ transform:'rotate(-90deg)', color:'var(--text-muted)' }}/>
          </div>
          <div style={{ padding:'16px 20px', display:'flex', gap:8, flexWrap:'wrap' }}>
            {[['PDF','📄'],['XLS','📊'],['DOC','📝'],['ODS','📋'],['ODT','📄'],['ODP','📊']].map(([label,icon]) => (
              <button key={label} className="pd-btn-config" onClick={() => toast(`Export ${label} en cours de développement`)}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'10px 16px', minWidth:64 }}>
                <span style={{ fontSize:20 }}>{icon}</span>
                <span style={{ fontSize:'0.7rem' }}>{label}</span>
              </button>
            ))}
            <button className="pd-btn-config" onClick={() => { loadProject(); toast.success('Actualisation effectuée'); }}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'10px 16px', minWidth:64 }}>
              <span style={{ fontSize:20 }}>🔄</span>
              <span style={{ fontSize:'0.7rem' }}>Actualiser</span>
            </button>
          </div>
          <div style={{ padding:'12px 20px 20px', color:'var(--text-faint)', fontSize:'0.875rem', textAlign:'center' }}>
            Les exports seront disponibles prochainement.
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* FACTURES D'ACHATS                                          */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'factures_a' && (() => {
        const STATUTS_INV = ['EN_ATTENTE','VALIDEE','PAYEE','ANNULEE'];
        const statutInvColor = (s) => ({ EN_ATTENTE:{color:'#F59E0B',bg:'rgba(245,158,11,0.10)'}, VALIDEE:{color:'#3B82F6',bg:'rgba(59,130,246,0.10)'}, PAYEE:{color:'#16A34A',bg:'rgba(34,197,94,0.10)'}, ANNULEE:{color:'#EF4444',bg:'rgba(239,68,68,0.10)'}  }[s] || {color:'#8B8FA8',bg:'rgba(139,143,168,0.12)'});
        return (<>
          {/* Formulaire */}
          {showInvForm && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--border)' }}>
                <div className="pd-section-title" style={{ marginBottom:0 }}>{editInvId ? 'Modifier la facture' : 'Nouvelle facture d\'achat'}</div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10, marginBottom:12 }}>
                  <div className="pd-field">
                    <div className="pd-field-label">Type</div>
                    <select className="form-control" value={invForm.type_facture} onChange={e=>setInvForm(f=>({...f,type_facture:e.target.value}))}>
                      <option value="ACHAT">Facture d'achat</option>
                      <option value="AVOIR_FOURNISSEUR">Avoir fournisseur</option>
                    </select>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Réf. externe</div>
                    <input className="form-control" value={invForm.ref_externe} onChange={e=>setInvForm(f=>({...f,ref_externe:e.target.value}))} placeholder="FAC-001"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Date</div>
                    <input type="date" className="form-control" value={invForm.date_facture} onChange={e=>setInvForm(f=>({...f,date_facture:e.target.value}))}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Émetteur</div>
                    <input className="form-control" value={invForm.emetteur} onChange={e=>setInvForm(f=>({...f,emetteur:e.target.value}))} placeholder="Fournisseur…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Destinataire</div>
                    <input className="form-control" value={invForm.destinataire} onChange={e=>setInvForm(f=>({...f,destinataire:e.target.value}))} placeholder="Wikeys…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Montant HT</div>
                    <input type="number" className="form-control" value={invForm.montant_ht} onChange={e=>setInvForm(f=>({...f,montant_ht:e.target.value}))} placeholder="0"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">TVA %</div>
                    <input type="number" className="form-control" value={invForm.taux_tva} onChange={e=>setInvForm(f=>({...f,taux_tva:e.target.value}))} placeholder="20"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Montant TTC</div>
                    <input type="number" className="form-control" value={invForm.montant_ttc} onChange={e=>setInvForm(f=>({...f,montant_ttc:e.target.value}))} placeholder="0"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Montant payé</div>
                    <input type="number" className="form-control" value={invForm.montant_paye} onChange={e=>setInvForm(f=>({...f,montant_paye:e.target.value}))} placeholder="0"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Montant HT projet</div>
                    <input type="number" className="form-control" value={invForm.montant_ht_projet} onChange={e=>setInvForm(f=>({...f,montant_ht_projet:e.target.value}))} placeholder="0"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Statut</div>
                    <select className="form-control" value={invForm.statut} onChange={e=>setInvForm(f=>({...f,statut:e.target.value}))}>
                      {STATUTS_INV.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="pd-field" style={{ gridColumn:'1/-1' }}>
                    <div className="pd-field-label">Description</div>
                    <input className="form-control" value={invForm.description} onChange={e=>setInvForm(f=>({...f,description:e.target.value}))} placeholder="Description…"/>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setShowInvForm(false); setEditInvId(null); setInvForm(EMPTY_INV); }}>Annuler</button>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveInv} disabled={savingInv}>
                    <Save size={13}/> {savingInv ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tableau factures */}
          <div className="card">
            <div style={{ padding:'12px 20px 10px', borderBottom:'1px solid var(--border)' }}>
              <div className="pd-section-title" style={{ marginBottom:0 }}>Factures</div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Réf. ext.</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Émetteur</th>
                    <th>Destinataire</th>
                    <th style={{ textAlign:'right' }}>Montant HT</th>
                    <th style={{ textAlign:'right' }}>Montant TTC</th>
                    <th style={{ textAlign:'right' }}>Montant payé</th>
                    <th style={{ textAlign:'right' }}>Montant impayé</th>
                    <th style={{ textAlign:'right' }}>Montant HT projet</th>
                    <th>Statut</th>
                    {canEdit && <th/>}
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr><td colSpan={13} style={{ textAlign:'center', padding:40, color:'var(--text-faint)' }}>Aucune donnée trouvée</td></tr>
                  ) : invoices.map(inv => {
                    const sc = statutInvColor(inv.statut);
                    return (
                      <tr key={inv.id}>
                        <td style={{ fontSize:'0.8125rem', color:'var(--orange-500)', fontWeight:600, cursor:'pointer' }} onClick={() => startEditInv(inv)}>
                          {inv.ref_externe || String(inv.id).slice(0,6).toUpperCase()}
                        </td>
                        <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{inv.type_facture === 'AVOIR_FOURNISSEUR' ? 'Avoir' : 'Achat'}</td>
                        <td style={{ fontSize:'0.8125rem', whiteSpace:'nowrap' }}>{inv.date_facture ? fmt(inv.date_facture) : '—'}</td>
                        <td style={{ fontSize:'0.8125rem', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.description || '—'}</td>
                        <td style={{ fontSize:'0.8125rem' }}>{inv.emetteur || '—'}</td>
                        <td style={{ fontSize:'0.8125rem' }}>{inv.destinataire || '—'}</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(inv.montant_ht || 0)}</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(inv.montant_ttc || 0)}</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(inv.montant_paye || 0)}</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(inv.montant_impaye || 0)}</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{inv.montant_ht_projet != null ? fmtNum(inv.montant_ht_projet) : '—'}</td>
                        <td><span style={{ padding:'3px 8px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, color:sc.color, background:sc.bg }}>{inv.statut}</span></td>
                        {canEdit && <td>
                          <div style={{ display:'flex', gap:4 }}>
                            <button className="ts-btn-remove" onClick={() => startEditInv(inv)}><Pencil size={13}/></button>
                            <button className="ts-btn-remove" onClick={() => handleDeleteInv(inv.id)}><Trash2 size={13}/></button>
                          </div>
                        </td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>);
      })()}

      {activeTab === 'factures_v' && (
        <ProjectSalesInvoices project={project} canEdit={canEdit} />
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* CONTRATS (VENTE + ACHAT partagent le même composant)       */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {(activeTab === 'contrats_v' || activeTab === 'contrats_a') && (() => {
        const isVente = activeTab === 'contrats_v';
        const contracts = isVente ? contractsV : contractsA;
        const STATUTS_CONTRAT = ['OUVERT', 'FERME', 'ANNULE', 'EN_ATTENTE'];
        const statutColor = (s) => ({
          OUVERT:     { color:'#3B82F6', bg:'rgba(59,130,246,0.10)' },
          FERME:      { color:'#8B8FA8', bg:'rgba(139,143,168,0.12)' },
          ANNULE:     { color:'#EF4444', bg:'rgba(239,68,68,0.10)' },
          EN_ATTENTE: { color:'#F59E0B', bg:'rgba(245,158,11,0.10)' },
        }[s] || { color:'#3B82F6', bg:'rgba(59,130,246,0.10)' });

        return (
          <div className="card">
            <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--border)' }}>
              <div className="pd-section-title" style={{ marginBottom:0 }}>CONTRATS</div>
            </div>

            {/* Formulaire ajout/édition */}
            {showContractForm && (
              <div style={{ padding:16, borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10, marginBottom:10 }}>
                  <div className="pd-field" style={{ gridColumn:'1/-1' }}>
                    <div className="pd-field-label">Nom du contrat</div>
                    <input className="form-control" value={contractForm.nom}
                      onChange={e => setContractForm(f=>({...f,nom:e.target.value}))} placeholder="Nom…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Réf. externe</div>
                    <input className="form-control" value={contractForm.ref_externe}
                      onChange={e => setContractForm(f=>({...f,ref_externe:e.target.value}))} placeholder="Réf…"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Catégorie</div>
                    <input className="form-control" value={contractForm.categorie}
                      onChange={e => setContractForm(f=>({...f,categorie:e.target.value}))}
                      placeholder={isVente ? 'Prestation forfaitaire ventes' : 'Prestation forfaitaire achats'}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Date du contrat</div>
                    <input type="date" className="form-control" value={contractForm.date_contrat}
                      onChange={e => setContractForm(f=>({...f,date_contrat:e.target.value}))}/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Total général</div>
                    <input type="number" className="form-control" value={contractForm.total_general}
                      onChange={e => setContractForm(f=>({...f,total_general:e.target.value}))} placeholder="0"/>
                  </div>
                  <div className="pd-field">
                    <div className="pd-field-label">Statut</div>
                    <select className="form-control" value={contractForm.statut}
                      onChange={e => setContractForm(f=>({...f,statut:e.target.value}))}>
                      {STATUTS_CONTRAT.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setShowContractForm(false); setEditContractId(null); setContractForm(EMPTY_CONTRACT); }}>Annuler</button>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveContract} disabled={savingContract || !contractForm.nom.trim()}>
                    <Save size={13}/> {savingContract ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            )}

            {contracts.length === 0 && !showContractForm ? (
              <div style={{ padding:48, textAlign:'center', color:'var(--text-faint)', fontSize:'0.875rem' }}>
                Aucun contrat {isVente ? 'de vente' : "d'achat"}
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Réf.</th>
                      <th>Nom du contrat</th>
                      <th>Réf. externe</th>
                      <th>Catégorie</th>
                      <th>Date du contrat</th>
                      <th style={{ textAlign:'right' }}>Total général</th>
                      <th>Statut</th>
                      {canEdit && <th style={{ width:60 }}/>}
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map(c => {
                      const sc = statutColor(c.statut);
                      return (
                        <tr key={c.id}>
                          <td>
                            <span style={{ color:'var(--accent)', fontWeight:600, fontSize:'0.8125rem', cursor:'pointer', textDecoration:'underline' }}
                              onClick={() => startEditContract(c)}>
                              {String(c.id).slice(0,6).toUpperCase()}
                            </span>
                          </td>
                          <td style={{ fontWeight:600, fontSize:'0.875rem' }}>{c.nom}</td>
                          <td style={{ fontSize:'0.8125rem', color:'var(--text-faint)' }}>{c.ref_externe || '—'}</td>
                          <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{c.categorie || '—'}</td>
                          <td style={{ fontSize:'0.8125rem', whiteSpace:'nowrap' }}>
                            {c.date_contrat ? fmt(c.date_contrat) : '—'}
                          </td>
                          <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:'0.875rem' }}>
                            {fmtNum(c.total_general ?? 0, 0)}
                          </td>
                          <td>
                            <span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:700,
                              color:sc.color, background:sc.bg, border:`1px solid ${sc.color}33`, whiteSpace:'nowrap' }}>
                              {c.statut.charAt(0) + c.statut.slice(1).toLowerCase().replace('_',' ')}
                            </span>
                          </td>
                          {canEdit && (
                            <td>
                              <div style={{ display:'flex', gap:4 }}>
                                <button className="ts-btn-remove" onClick={() => startEditContract(c)} title="Modifier"><Pencil size={13}/></button>
                                <button className="ts-btn-remove" onClick={() => handleDeleteContract(c.id)} title="Supprimer"><Trash2 size={13}/></button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {showEInvoiceConfig && (
        <EInvoiceConfigModal
          project={project}
          onClose={() => setShowEInvoiceConfig(false)}
        />
      )}

    </div>
  );
}
